// src/pages/ContactsPage.tsx — Phase 1, rebuilt against the heritage design system (Slice A).
//
// Lists every contact from /api/contacts. Phone visibility is enforced
// server-side via the filterPhone chokepoint (api.md §4.2, primer §10);
// the page renders whatever the API returned verbatim — never re-filter
// client-side.
//
// Admin actions (Create / Edit / Delete) open ContactModal. Local list
// state is only mutated after the server confirms (pessimistic update).
//
// Visual: matches docs/prototype/apps1.jsx ContactsApp. No pinned section
// (prototype's pin feature was local-only; real /api/pins/* is Phase 4+).
//
// Phase 9 follow-up (2026-05-21):
//   - Emergency ✚ button is now gated. Only HR Admins (me.isHrAdmin) see
//     it on directory contacts; on personal contacts, the owner sees it
//     (since they entered the data). Everyone else gets no ✚, matching
//     Chris's "emergency contact security group" requirement.
//   - Filter pills row added below the search input. Categories: All /
//     Personal / Manager / DM / MOM / EM / First aider. Filters apply to
//     both the pinned section and the main list; the pinned section hides
//     entirely when a filter other than "all" is active (the user is
//     scoping their view; the pinned shortcut would be noise).
//   - "First aider" Stamp on each contact row AND the matching filter pill
//     are now gated on me.apps.firstAider. Defaults off; admin must grant
//     per-user. Useful information that not every casual volunteer needs
//     to see by default. The data field (contact.isFirstAider) still
//     arrives in /api/contacts responses unchanged — the gate is purely
//     UI-level surfacing.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../auth";
import { apiFetch, ApiError } from "../api";
import { C, F, layout } from "../theme";
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Icons,
  isWorkHours,
  SearchInput,
  Stamp,
  TopBar,
} from "../components/ui";
import {
  ContactModal,
  type ContactModalMode,
  type ContactRow,
} from "../components/ContactModal";
import {
  ComposeSheet,
  ContactQmButton,
} from "../components/quickMessage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ContactListItem = ContactRow;

interface ContactsListResponse {
  contacts: ContactListItem[];
}

function sortContacts(list: ContactListItem[]): ContactListItem[] {
  return [...list].sort(
    (a, b) =>
      a.last.localeCompare(b.last) || a.first.localeCompare(b.first),
  );
}

// ---------------------------------------------------------------------------
// Filter pills — Phase 9 follow-up (2026-05-21)
// ---------------------------------------------------------------------------

type ContactFilterId = "all" | "personal" | "manager" | "dm" | "mom" | "em" | "signed-on" | "first-aider";

const CONTACT_FILTERS: ReadonlyArray<{ id: ContactFilterId; label: string }> = [
  { id: "all",         label: "All" },
  { id: "personal",    label: "Personal" },
  { id: "manager",     label: "Manager" },
  { id: "dm",          label: "DM" },
  { id: "mom",         label: "MOM" },
  { id: "em",          label: "EM" },
  // Sign-In System: contacts whose linked login is currently on duty.
  { id: "signed-on",   label: "Signed-On" },
  { id: "first-aider", label: "First aider" },
];

function matchesFilter(c: ContactListItem, f: ContactFilterId): boolean {
  switch (f) {
    case "all":         return true;
    case "personal":    return c.category === "personal";
    case "manager":     return Boolean(c.isManager);
    case "dm":          return Boolean(c.isDM);
    case "mom":         return Boolean(c.isMOM);
    case "em":          return Boolean(c.isEM);
    case "signed-on":   return Boolean(c.signedOn);
    case "first-aider": return Boolean(c.isFirstAider);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const iconBtnStyle = (bg: string, bd: string): React.CSSProperties => ({
  width: 38,
  height: 38,
  borderRadius: 11,
  background: bg,
  border: `1px solid ${bd}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  textDecoration: "none",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
  flexShrink: 0,
});

function avatarTone(c: ContactListItem): "heather" | "brass" | "sage" {
  if (c.isDM) return "heather";
  if (c.isMOM) return "brass";
  if (c.isEM) return "sage";
  return "brass";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ContactsPage() {
  const { me } = useCurrentUser();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ContactListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterId, setFilterId] = useState<ContactFilterId>("all");
  const [modal, setModal] = useState<ContactModalMode | null>(null);
  const [emergencyId, setEmergencyId] = useState<number | null>(null);
  // Phase 7 -- Quick Message compose sheet target. Null = sheet closed.
  const [composeFor, setComposeFor] = useState<ContactListItem | null>(null);
  const [qmToast, setQmToast] = useState<string | null>(null);
  const workHrs = isWorkHours();

  useEffect(() => {
    if (!qmToast) return;
    const id = window.setTimeout(() => setQmToast(null), 3000);
    return () => window.clearTimeout(id);
  }, [qmToast]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<ContactsListResponse>("/contacts")
      .then((data) => {
        if (!cancelled) setContacts(sortContacts(data.contacts));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof ApiError ? e.message : "Failed to load contacts";
        setError(message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isAdmin = me?.isAdmin ?? false;
  const isHrAdmin = me?.isHrAdmin ?? false;
  // Phase 11 / Q3 — contact-editor role widens directory write access.
  // Personal contacts still go through the owner gate below; this flag
  // unlocks edits on `nymr` / `volunteer` categories.
  const isContactEditor = me?.isContactEditor ?? false;
  const viewerHubUserId = me?.hubUserId ?? null;
  // First-aider stamp + filter pill are visible to ALL users (Chris,
  // 2026-05-29). Previously gated behind the per-user apps.firstAider
  // permission; now everyone can see who's a first aider and filter by it.
  const canSeeFirstAider = true;

  // Phase 9 / Phase 11 — a row is editable when:
  //   * the viewer is an admin (anything), OR
  //   * the viewer is a Contact Editor and the row is a directory entry
  //     (category `nymr` or `volunteer`), OR
  //   * the row is a personal contact owned by the viewer.
  // Personal contacts stay scoped to their owner regardless of the
  // editor flag — matches the server-side gate in contacts.ts.
  const canEditRow = (c: ContactListItem) =>
    isAdmin ||
    (isContactEditor && (c.category === "nymr" || c.category === "volunteer")) ||
    (c.category === "personal" && c.ownerUserId !== null && c.ownerUserId === viewerHubUserId);

  // Phase 9 follow-up (2026-05-21) -- the ✚ Emergency-contact button is gated
  // to the "emergency contact security group". In practice that means:
  //   - HR Admins see ✚ on any contact with emergency data
  //   - Owners of a personal contact see ✚ on their own personal records
  //     (they entered the data, so they should be able to see it)
  //   - Everyone else (PIN volunteers viewing directory contacts, non-HR
  //     SSO users viewing directory contacts) gets no ✚.
  // The per-contact emergency_contact_visible_to grant lives on hub_users,
  // not hub_contacts, so it doesn't apply to the directory ✚ surface here.
  const canViewEmergencyFor = (c: ContactListItem): boolean => {
    if (!c.hasEmergency) return false;
    if (isHrAdmin) return true;
    if (
      c.category === "personal" &&
      c.ownerUserId !== null &&
      c.ownerUserId === viewerHubUserId
    ) {
      return true;
    }
    return false;
  };

  const filtered = useMemo(() => {
    if (!contacts) return null;
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (!matchesFilter(c, filterId)) return false;
      if (!q) return true;
      const haystack = [c.first, c.last, c.role ?? "", c.location ?? ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [contacts, search, filterId]);

  const isSearching = search.trim().length > 0;
  const isFiltering = filterId !== "all";
  const pinned = useMemo(
    () =>
      contacts
        ? contacts.filter((c) => c.isPinned && matchesFilter(c, filterId))
        : [],
    [contacts, filterId],
  );

  /** Optimistic pin/unpin — flip local state, fire the request, revert on error. */
  async function togglePin(contact: ContactListItem) {
    const willBePinned = !contact.isPinned;
    setContacts((prev) =>
      prev
        ? prev.map((c) => (c.id === contact.id ? { ...c, isPinned: willBePinned } : c))
        : prev,
    );
    try {
      await apiFetch(`/contacts/${contact.id}/pin`, {
        method: willBePinned ? "POST" : "DELETE",
      });
    } catch {
      // Revert
      setContacts((prev) =>
        prev
          ? prev.map((c) => (c.id === contact.id ? { ...c, isPinned: !willBePinned } : c))
          : prev,
      );
    }
  }

  const personalCount = useMemo(
    () =>
      contacts
        ? contacts.filter((c) => c.category === "personal" && c.ownerUserId === viewerHubUserId).length
        : 0,
    [contacts, viewerHubUserId],
  );
  const PERSONAL_SOFT_CAP = 50;
  const PERSONAL_HARD_CAP = 200;
  const showSoftCapWarning = personalCount >= PERSONAL_SOFT_CAP && personalCount < PERSONAL_HARD_CAP;
  const atHardCap = personalCount >= PERSONAL_HARD_CAP;

  const emergencyContact = emergencyId
    ? contacts?.find((c) => c.id === emergencyId) ?? null
    : null;

  if (emergencyContact) {
    return (
      <EmergencyDetail
        contact={emergencyContact}
        onBack={() => setEmergencyId(null)}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.paper }}>
      <TopBar
        title="Key Contacts"
        sub="Tap to call, email, or pin"
        onBack={() => navigate("/")}
      />

      <div
        style={{
          maxWidth: layout.maxContentPx,
          margin: "0 auto",
          padding: "14px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >

        <Button
          variant="soft"
          onClick={() => setModal({ kind: "create", category: "personal" })}
          disabled={atHardCap}
          style={atHardCap ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        >
          <Icons.Plus s={18} c={C.brassDk} /> Add personal contact
        </Button>

        {showSoftCapWarning && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: C.brassBg,
              border: `1px solid ${C.brassLt}`,
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13,
              color: C.brassDk,
              lineHeight: 1.45,
            }}
          >
            You have {personalCount} personal contacts — approaching the {PERSONAL_HARD_CAP} limit.
          </div>
        )}
        {atHardCap && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
              color: C.signalDk,
            }}
          >
            You've hit the {PERSONAL_HARD_CAP} personal-contact limit. Delete unused contacts to add new ones.
          </div>
        )}

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, role or station"
        />

        <ContactFilterPills
          active={filterId}
          canSeeFirstAider={canSeeFirstAider}
          onChange={setFilterId}
        />

        {!workHrs && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              background: C.paperWarm,
              border: `1px solid ${C.hairlineLt}`,
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13,
              color: C.inkSoft,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icons.Pin s={14} c={C.brassDk} /> Phone visibility depends on your role and the time of day.
          </div>
        )}

        {(isAdmin || isContactEditor) && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              background: C.heatherBg,
              border: "1px solid rgba(122,93,140,0.22)",
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12.5,
              color: C.heatherDk,
              textAlign: "center",
            }}
          >
            {isAdmin
              ? "Tap any contact to edit"
              : "Tap a directory contact to edit"}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: C.signalBg,
              border: "1px solid rgba(184,58,43,0.22)",
              color: C.signalDk,
              fontFamily: F.body,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        )}

        {!contacts && !error && (
          <div
            style={{
              padding: "24px 0",
              textAlign: "center",
              fontFamily: F.serif,
              fontStyle: "italic",
              color: C.inkMute,
            }}
          >
            Loading contacts…
          </div>
        )}

        {!isSearching && !isFiltering && pinned.length > 0 && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "0 2px 8px",
              }}
            >
              <Eyebrow>Pinned</Eyebrow>
              <Stamp tone="sage">{pinned.length}</Stamp>
            </div>
            <Card padded={false}>
              <div style={{ padding: "4px 16px" }}>
                {pinned.map((c, i) => (
                  <ContactListRow
                    key={c.id}
                    contact={c}
                    isFirst={i === 0}
                    canEdit={canEditRow(c)}
                    canViewEmergency={canViewEmergencyFor(c)}
                    canSeeFirstAider={canSeeFirstAider}
                    onEdit={() => setModal({ kind: "edit", contact: c })}
                    onEmergency={() => setEmergencyId(c.id)}
                    onTogglePin={() => togglePin(c)}
                    onQuickMessage={() => setComposeFor(c)}
                  />
                ))}
              </div>
            </Card>
          </div>
        )}

        {filtered && (
          <Card padded={false}>
            <div style={{ padding: "4px 16px" }}>
              {filtered.map((c, i) => (
                <ContactListRow
                  key={c.id}
                  contact={c}
                  isFirst={i === 0}
                  canEdit={canEditRow(c)}
                  canViewEmergency={canViewEmergencyFor(c)}
                  canSeeFirstAider={canSeeFirstAider}
                  onEdit={() => setModal({ kind: "edit", contact: c })}
                  onEmergency={() => setEmergencyId(c.id)}
                  onTogglePin={() => togglePin(c)}
                  onQuickMessage={() => setComposeFor(c)}
                />
              ))}
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: "30px 0",
                    textAlign: "center",
                    color: C.inkMute,
                    fontFamily: F.serif,
                    fontStyle: "italic",
                  }}
                >
                  {isSearching ? (
                    <>No contacts match &ldquo;{search.trim()}&rdquo;</>
                  ) : isFiltering ? (
                    <>No contacts in this filter</>
                  ) : (
                    "No contacts yet"
                  )}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {modal && (
        <ContactModal
          mode={modal}
          onClose={() => setModal(null)}
          onRequestDelete={(contact) =>
            setModal({ kind: "delete-confirm", contact })
          }
          onSaved={(op) => {
            setContacts((prev) => {
              if (!prev) return prev;
              if (op.kind === "created") return sortContacts([...prev, op.row]);
              if (op.kind === "updated") {
                // Preserve isPinned from the existing row — the modal doesn't
                // round-trip it because pinning is independent of contact edits.
                return sortContacts(
                  prev.map((c) =>
                    c.id === op.row.id ? { ...op.row, isPinned: c.isPinned } : c,
                  ),
                );
              }
              return prev.filter((c) => c.id !== op.id);
            });
            if (op.kind === "created" && op.warning) {
              setQmToast(op.warning);
            }
            setModal(null);
          }}
        />
      )}

      {composeFor && composeFor.hubUserId !== null && (
        <ComposeSheet
          open
          recipient={{
            hubUserId: composeFor.hubUserId,
            firstName: composeFor.first,
            lastName: composeFor.last,
          }}
          onCancel={() => setComposeFor(null)}
          onSent={(info) => {
            setComposeFor(null);
            setQmToast(`Sent to ${info.firstName}.`);
          }}
        />
      )}

      {qmToast && (
        <div
          role="status"
          style={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: 80,
            zIndex: 250,
            padding: "12px 14px",
            borderRadius: 12,
            background: C.sageBg,
            border: `1px solid ${C.sageLt}`,
            color: C.sageDk,
            fontFamily: F.body,
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: 0.3,
            textAlign: "center",
            maxWidth: layout.maxContentPx,
            marginLeft: "auto",
            marginRight: "auto",
            boxShadow: "0 8px 22px rgba(122,84,11,0.18)",
          }}
        >
          {qmToast}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter pills — inline component mirroring AccessUsersPage's FilterPills.
// Phase 9 follow-up: hides the "First aider" pill when the viewer lacks
// the apps.firstAider permission (matches the per-row Stamp gate).
// ---------------------------------------------------------------------------

function ContactFilterPills({
  active,
  canSeeFirstAider,
  onChange,
}: {
  active: ContactFilterId;
  canSeeFirstAider: boolean;
  onChange: (f: ContactFilterId) => void;
}) {
  const filters = canSeeFirstAider
    ? CONTACT_FILTERS
    : CONTACT_FILTERS.filter((f) => f.id !== "first-aider");
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {filters.map((f) => {
        const isOn = f.id === active;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(f.id)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              border: isOn ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
              background: isOn ? C.brassBg : C.card,
              color: isOn ? C.brassDk : C.inkSoft,
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.2,
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ContactListRow({
  contact,
  isFirst,
  canEdit,
  canViewEmergency,
  canSeeFirstAider,
  onEdit,
  onEmergency,
  onTogglePin,
  onQuickMessage,
}: {
  contact: ContactListItem;
  isFirst: boolean;
  canEdit: boolean;
  canViewEmergency: boolean;
  canSeeFirstAider: boolean;
  onEdit: () => void;
  onEmergency: () => void;
  onTogglePin: () => void;
  onQuickMessage: () => void;
}) {
  const isPersonal = contact.category === "personal";
  return (
    <div
      onClick={() => (canEdit ? onEdit() : undefined)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 0",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
        cursor: canEdit ? "pointer" : "default",
        ...(isPersonal && {
          borderLeft: `3px solid ${C.heather}`,
          paddingLeft: 10,
          marginLeft: -10,
        }),
      }}
    >
      <Avatar
        first={contact.first}
        last={contact.last}
        size={42}
        tone={isPersonal ? "heather" : avatarTone(contact)}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: F.serif,
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {contact.first} {contact.last}
          </span>
          {isPersonal && <Stamp tone="heather" style={{ flexShrink: 0 }}>Personal</Stamp>}
          {!isPersonal && contact.isDM && <Stamp tone="heather" style={{ flexShrink: 0 }}>DM</Stamp>}
          {!isPersonal && contact.isMOM && <Stamp tone="brass" style={{ flexShrink: 0 }}>MOM</Stamp>}
          {!isPersonal && contact.isEM && <Stamp tone="sage" style={{ flexShrink: 0 }}>EM</Stamp>}
          {!isPersonal && contact.isDF && <Stamp tone="heather" style={{ flexShrink: 0 }}>DF</Stamp>}
          {contact.signedOn && (
            <Stamp tone="sage" style={{ flexShrink: 0 }}>Signed-In</Stamp>
          )}
          {contact.isFirstAider && canSeeFirstAider && (
            <Stamp tone="signal" style={{ flexShrink: 0 }}>First aider</Stamp>
          )}
        </div>
        {(contact.role || contact.location) && (
          <div
            style={{
              fontSize: 12.5,
              color: C.inkSoft,
              fontWeight: 600,
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: F.body,
            }}
          >
            {[contact.role, contact.location].filter(Boolean).join(" · ")}
          </div>
        )}
        {contact.phone && (
          <div
            style={{
              fontFamily: F.mono,
              fontSize: 12.5,
              color: C.brassDk,
              fontWeight: 700,
              marginTop: 3,
              letterSpacing: 0.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {contact.phone}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          aria-label={contact.isPinned ? `Unpin ${contact.first} ${contact.last}` : `Pin ${contact.first} ${contact.last}`}
          aria-pressed={contact.isPinned}
          style={{
            ...iconBtnStyle(
              contact.isPinned ? C.brassDk : C.brassBg,
              contact.isPinned ? C.brassDk : C.brassLt,
            ),
            width: 32,
            height: 32,
          }}
        >
          <Icons.Pin s={14} c={contact.isPinned ? C.brassGlow : C.brassDk} />
        </button>
        {canViewEmergency && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEmergency();
            }}
            aria-label="Emergency contact"
            style={{
              ...iconBtnStyle(C.signalBg, "rgba(184,58,43,0.25)"),
              width: 34,
              height: 34,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 800, color: C.signalDk, lineHeight: 1 }}>✚</span>
          </button>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Call"
            style={{
              ...iconBtnStyle(C.brassBg, C.brassLt),
              width: 34,
              height: 34,
            }}
          >
            <Icons.Phone s={15} c={C.brassDk} />
          </a>
        )}
        {contact.hubUserId !== null && (
          <ContactQmButton
            onClick={onQuickMessage}
            ariaLabel={`Send quick message to ${contact.first} ${contact.last}`}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Emergency-detail screen (inline route — no separate page)
// ---------------------------------------------------------------------------

function EmergencyDetail({
  contact,
  onBack,
}: {
  contact: ContactListItem;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<{
    name: string;
    relationship: string | null;
    phone: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<{
      emergency: { name: string; relationship: string | null; phone: string | null } | null;
    }>(`/contacts/${contact.id}`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d.emergency ?? null);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : "Failed to load emergency contact");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contact.id]);

  return (
    <div style={{ minHeight: "100vh", background: C.paper }}>
      <TopBar
        title="Emergency contact"
        sub={`For ${contact.first} ${contact.last}`}
        onBack={onBack}
      />
      <div
        style={{
          maxWidth: layout.maxContentPx,
          margin: "0 auto",
          padding: "16px 16px 24px",
        }}
      >
        {error && (
          <Card>
            <div
              style={{
                fontFamily: F.body,
                fontSize: 14,
                fontWeight: 700,
                color: C.signalDk,
              }}
            >
              {error}
            </div>
          </Card>
        )}
        {!error && loading && (
          <Card>
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
              }}
            >
              Loading…
            </div>
          </Card>
        )}
        {!error && !loading && !detail && (
          <Card>
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
              }}
            >
              No emergency contact on file
            </div>
          </Card>
        )}
        {!error && !loading && detail && (
          <Card style={{ borderColor: "rgba(184,58,43,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: C.signalBg,
                  border: "1px solid rgba(184,58,43,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: 22, fontWeight: 800, color: C.signalDk }}>✚</span>
              </div>
              <div>
                <Eyebrow c={C.signalDk}>Emergency contact</Eyebrow>
                <div
                  style={{
                    fontFamily: F.serif,
                    fontSize: 22,
                    fontWeight: 700,
                    color: C.ink,
                    letterSpacing: -0.3,
                  }}
                >
                  {detail.name}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "14px 16px",
                background: C.signalBg,
                borderRadius: 12,
              }}
            >
              {detail.relationship && (
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: C.signalDk,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  {detail.relationship}
                </div>
              )}
              {detail.phone && (
                <div
                  style={{
                    fontFamily: F.mono,
                    fontSize: 18,
                    fontWeight: 800,
                    color: C.ink,
                    letterSpacing: 0.5,
                  }}
                >
                  {detail.phone}
                </div>
              )}
            </div>
            {detail.phone && (
              <a
                href={`tel:${detail.phone}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontFamily: F.body,
                  fontWeight: 800,
                  fontSize: 15,
                  padding: "12px 16px",
                  borderRadius: 12,
                  minHeight: 48,
                  marginTop: 14,
                  width: "100%",
                  background: C.brassDk,
                  color: C.brassGlow,
                  textDecoration: "none",
                  boxShadow:
                    "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)",
                  letterSpacing: 0.2,
                }}
              >
                Call now
              </a>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
